import AmountSelector from '@/components/AmountSelector/AmountSelector'
import DateInput from '@/components/DateInput/DateInput'
import Input from '@/components/Input/Input'
import Select from '@/components/Select/Select'
import Textarea from '@/components/Textarea/Textarea'
import classNames from 'classnames'
import FormFieldError from './FormFieldError/FormFieldError'
import styles from './FormField.module.scss'
import Toggle from '../Toggle/Toggle'

export type FormFieldChangeEvent =
  | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  | undefined
export type FormFieldProps = {
  ariaLabel?: string
  className?: string
  errorMessage?: string
  id?: string
  items?: (string | undefined)[]
  label?: string
  max?: string
  maxLength?: number
  min?: string
  name: string
  onBlur?: (e: FormFieldChangeEvent) => void
  onChange: (e: FormFieldChangeEvent) => void
  placeholder?: string | undefined
  required?: boolean
  type?:
    | 'text'
    | 'email'
    | 'textarea'
    | 'select'
    | 'date'
    | 'time'
    | 'toggle'
    | 'amount'
    | 'tel'
    | 'url'
    | 'search'
    | 'password'
    | 'number'
    | 'month'
    | 'week'
  value: string
  variant?: 'default' | 'minimal'
  autoComplete?: React.HTMLInputAutoCompleteAttribute
}

const FormField = ({
  ariaLabel,
  className,
  errorMessage,
  id,
  items,
  label,
  max,
  maxLength,
  min,
  name,
  onBlur,
  onChange,
  placeholder,
  required,
  type,
  value,
  variant = 'default',
  ...others
}: FormFieldProps) => {
  let inputComponent
  const inputProps = {
    className: classNames(
      styles.formInputText,
      { [styles.formInputTextError]: errorMessage },
      { [styles.formInputTextMinimal]: variant === 'minimal' },
      className,
    ),
    id,
    max,
    maxLength,
    min,
    name,
    onBlur,
    onChange,
    placeholder,
    value,
    ...others,
  }

  switch (type) {
    case 'text':
    case 'email':
    case 'tel':
    case 'url':
    case 'search':
    case 'password':
    case 'number':
    case 'month':
    case 'week':
      inputComponent = (
        <Input
          {...inputProps}
          type={type}
        />
      )
      break
    case 'textarea':
      inputComponent = (
        <Textarea
          {...inputProps}
          className={classNames(inputProps.className, styles.textArea)}
        />
      )
      break
    case 'select':
      if (Array.isArray(items)) {
        inputComponent = (
          <Select
            {...inputProps}
            ariaLabel={ariaLabel}
            items={items}
          />
        )
      }
      break
    case 'date':
      inputComponent = <DateInput {...inputProps} />
      break
    case 'time':
      inputComponent = (
        <Input
          {...inputProps}
          type="time"
        />
      )
      break
    case 'toggle':
      if (Array.isArray(items)) {
        inputComponent = (
          <Toggle
            {...inputProps}
            items={items}
          />
        )
      }
      break
    case 'amount':
      inputComponent = (
        <AmountSelector
          {...inputProps}
          amounts={items || []}
        />
      )
      break
    default:
      inputComponent = null
  }

  return (
    <div className={styles.FormField}>
      {label && (
        <label className={variant === 'minimal' ? 'sr-only' : undefined}>
          {required && '*'} {label}
        </label>
      )}
      {inputComponent}
      <FormFieldError message={errorMessage} />
    </div>
  )
}

export default FormField
